import React, { useState } from 'react';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Field, Input } from '../ui';
import { AUTH_BUTTON, AuthActions, AuthScreen, FormNotice, PasswordInput, StepBar } from './AuthShell';

const STEPS = [
  { title: 'Your details', meta: 'First super admin account' },
  { title: 'Choose a password', meta: 'At least 8 characters' },
];

const emailError = (email: string): string => {
  if (!email) return 'Email is required';
  if (!/\S+@\S+\.\S+/.test(email)) return 'Invalid email address';
  return '';
};

interface SuperUserSetupFormProps {
  onSuccess?: () => void;
}

export const SuperUserSetupForm: React.FC<SuperUserSetupFormProps> = ({ onSuccess }) => {
  const { refreshSetupStatus } = useAuth();
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);

  const validate = () => {
    const e: Record<string, string> = {};
    
    const emailMessage = emailError(form.email);
    if (emailMessage) {
      e.email = emailMessage;
    }
    
    if (!form.password) {
      e.password = 'Password is required';
    } else if (form.password.length < 8) {
      e.password = 'Password must be at least 8 characters';
    }
    
    if (form.password !== form.confirmPassword) {
      e.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Step 1 only checks the email, with the same rule as `validate`
  const validateDetails = () => {
    const emailMessage = emailError(form.email);
    setErrors((prev) => ({ ...prev, email: emailMessage }));
    return !emailMessage;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 0) {
      if (validateDetails()) setStep(1);
      return;
    }

    setSubmitError('');
    setSubmitSuccess('');
    
    if (!validate()) {
      if (emailError(form.email)) setStep(0);
      return;
    }
    
    try {
      setLoading(true);
      const result = await authService.createSuperUser({
        email: form.email,
        password: form.password,
        firstName: form.firstName || undefined,
        lastName: form.lastName || undefined,
      });
      
      setSubmitSuccess(result.message);
      
      // Refresh setup status and call success callback after short delay
      setTimeout(async () => {
        await refreshSetupStatus();
        onSuccess?.();
      }, 2000);
    } catch (err: any) {
      console.error('Superuser creation error:', err);
      let message = 'Failed to create superuser. Please try again.';
      
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (err?.response?.status === 409) {
        message = 'A superuser already exists or this email is already registered.';
      } else if (err?.response?.status === 400) {
        if (err?.response?.data?.message?.includes('Maximum of 3 superadmins')) {
          message = 'Maximum of 3 superadmins allowed. Please contact system administrator.';
        } else {
          message = 'Invalid data provided. Please check your input and try again.';
        }
      } else if (err?.response?.status >= 500) {
        message = 'Server error occurred. Please try again later.';
      } else if (err?.code === 'NETWORK_ERROR' || !err?.response) {
        message = 'Network error. Please check your connection and try again.';
      }
      
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  const current = STEPS[step];

  return (
    <AuthScreen
      progress={<StepBar current={step} total={STEPS.length} />}
      title={current.title}
      meta={current.meta}
    >
      <form className="flex flex-1 flex-col" onSubmit={handleSubmit} noValidate>
        {step === 0 ? (
          <div key="details" className="animate-fade-up space-y-3.5">
            <Field label="Email" htmlFor="email" error={errors.email}>
              <Input
                id="email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={form.email}
                onChange={handleChange}
                aria-invalid={!!errors.email}
                placeholder="you@company.com"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" htmlFor="firstName">
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </Field>
              <Field label="Last name" htmlFor="lastName">
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </Field>
            </div>
          </div>
        ) : (
          <div key="password" className="animate-fade-up space-y-3.5">
            {/* Lets password managers pair the new password with the account */}
            <input type="email" name="username" autoComplete="username" value={form.email} readOnly hidden />
            <Field label="Password" htmlFor="password" error={errors.password}>
              <PasswordInput
                id="password"
                name="password"
                autoComplete="new-password"
                required
                autoFocus
                value={form.password}
                onChange={handleChange}
                aria-invalid={!!errors.password}
              />
            </Field>
            <Field label="Confirm password" htmlFor="confirmPassword" error={errors.confirmPassword}>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                required
                value={form.confirmPassword}
                onChange={handleChange}
                aria-invalid={!!errors.confirmPassword}
              />
            </Field>
          </div>
        )}

        <AuthActions>
          {submitError && <FormNotice>{submitError}</FormNotice>}
          {submitSuccess && <FormNotice tone="success">{submitSuccess}</FormNotice>}
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="secondary"
                onClick={() => setStep(0)}
                disabled={loading}
                className="h-10 flex-shrink-0 sm:h-9"
              >
                Back
              </Button>
            )}
            <Button type="submit" variant="primary" loading={loading} className={`${AUTH_BUTTON} flex-1`}>
              {step === 0 ? 'Continue' : loading ? 'Creating…' : 'Create account'}
            </Button>
          </div>
        </AuthActions>
      </form>
    </AuthScreen>
  );
};
